import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, Users, Baby, Flower2, Check, Loader2, Sparkles, CreditCard, Heart, Gift, FileText } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import DahliasShell from "../components/dahlias/DahliasShell";
import PatternBand from "../components/dahlias/PatternBand";
import { VisitTiles, FarmExperience, WhoYouMightMeet } from "../components/visit/VisitSections";
import { OpenDaysSection } from "../components/visit/OpenDays";
import { BookingCalendar } from "../components/visit/BookingCalendar";
import { BuddyBookings } from "../components/visit/BuddyBookings";

/* Private Pickings — public booking page. Calendar first: tap a day, pick a
   private session (3-10 paid guests) or a Buddy Booking for two, or request
   the date. Under-12s free, optional extras, pay by card or invoice. */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const prettyDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-NZ", {
  weekday: "long", day: "numeric", month: "long", year: "numeric",
});
const scrollTo = (id) => setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);

export default function PrivatePickings() {
  const [info, setInfo] = useState(null);
  const [pick, setPick] = useState(null); // { kind: "private"|"buddy", id, date, time, end?, price? }
  const [buddyMode, setBuddyMode] = useState(false);
  const [guests, setGuests] = useState(3);
  const [children, setChildren] = useState(0);
  const [over75, setOver75] = useState(0);
  const [extras, setExtras] = useState([]);
  const [payMethod, setPayMethod] = useState("card");
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [promo, setPromo] = useState({ code: "", applied: null, busy: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [payState, setPayState] = useState(""); // "", "checking", "paid", "unpaid", "cancelled"

  useEffect(() => {
    axios.get(`${API}/private-pickings/info`).then((r) => {
      setInfo(r.data);
      const first = (r.data.pricing || [])[0];
      if (first) setGuests(first.paid_guests);
    }).catch(() => {});
  }, []);

  // Return from Stripe (?booking=&email=&session_id=) or legacy calendar handoff (?slot=&date=).
  useEffect(() => {
    if (!info) return;
    const params = new URLSearchParams(window.location.search);
    const bookingId = params.get("booking"); const email = params.get("email"); const sessionId = params.get("session_id");
    if (bookingId && email) {
      const load = () => axios.get(`${API}/private-pickings/bookings/${bookingId}`, { params: { email } }).then((r) => setDone(r.data.booking));
      if (sessionId) {
        setPayState("checking");
        let tries = 0;
        const poll = async () => {
          try {
            const r = await axios.get(`${API}/payments/status/${sessionId}`);
            if (r.data.payment_status === "paid") { setPayState("paid"); await load(); toast.success("Payment received — thank you."); return; }
            if (r.data.status === "expired") { setPayState("unpaid"); await load(); return; }
          } catch { /* keep polling */ }
          if (++tries < 6) setTimeout(poll, 2000); else { setPayState("unpaid"); load(); }
        };
        poll();
      } else { setPayState(params.get("cancelled") ? "cancelled" : ""); load(); }
      scrollTo("private-booking-form");
      return;
    }
    const wantSlot = params.get("slot");
    if (!wantSlot) return;
    const live = (info.slots || []).find((s) => s.id === wantSlot);
    if (live && !live.taken) {
      setPick({ kind: "private", id: live.id, date: live.date, time: live.time });
      toast.success(`${prettyDate(live.date)} at ${live.time} is held in the form — complete your details below.`);
    } else toast.error("That time has just been booked or is no longer available — please choose another.");
    scrollTo("private-booking-form");
  }, [info]);

  const isBuddy = pick?.kind === "buddy";
  const buddyPrice = Number(info?.buddy?.price || 250);
  const guestCount = isBuddy ? 2 : guests;
  const buckets = Math.min(over75, guestCount);
  const priceFor = (n) => (info?.pricing || []).find((p) => p.paid_guests === n)?.price || 0;
  const extrasTotal = (info?.extras || []).filter((e) => extras.includes(e.key)).reduce((n, e) => n + e.price, 0);
  const total = (isBuddy ? buddyPrice : priceFor(guests)) + extrasTotal;
  const discount = promo.applied ? Math.min(total, promo.applied.kind === "percent" ? Math.round(total * promo.applied.value) / 100 : promo.applied.value) : 0;
  const payable = Math.max(0, total - discount);
  const depositPct = Number(info?.deposit_percent || 50);
  const deposit = Math.round(payable * depositPct) / 100;

  const checkPromo = async () => {
    if (!promo.code.trim()) return;
    setPromo((p) => ({ ...p, busy: true }));
    try {
      const r = await axios.post(`${API}/visit/promo/check`, { code: promo.code, booking_type: "private_picking", amount: total });
      setPromo({ code: r.data.code, applied: r.data, busy: false });
      toast.success(`Code applied — ${r.data.kind === "percent" ? `${r.data.value}% off` : `NZ$${r.data.value} off`}.`);
    } catch (e) { setPromo((p) => ({ ...p, applied: null, busy: false })); toast.error(e?.response?.data?.detail || "That code didn't work."); }
  };
  const toggleExtra = (key) => setExtras((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);

  const pickSlot = (s, date) => { setPick({ kind: "private", id: s.id, date, time: s.time }); scrollTo("private-booking-form"); };
  const pickBuddy = (b) => { setPick({ kind: "buddy", id: b.id, date: b.date, time: b.time, end: b.end, remaining: b.remaining, price: b.price }); setChildren(0); scrollTo("private-booking-form"); };
  const findBuddy = () => { setBuddyMode(true); scrollTo("private-calendar"); };

  const submit = async () => {
    if (!pick) return toast.error("Choose a date and session on the calendar first.");
    if (!form.name.trim() || !form.email.trim()) return toast.error("We need your name and email.");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/private-pickings/bookings`, {
        ...form, slot_id: pick.id, paid_guests: guestCount, children: isBuddy ? 0 : children, extras, over_75: over75,
        payment_method: payMethod, promo_code: promo.applied ? promo.applied.code : "",
      });
      const booking = r.data.booking;
      setDone(booking);
      if (payMethod === "card") {
        try {
          const p = await axios.post(`${API}/private-pickings/bookings/${booking.id}/pay`, { email: booking.email, amount_kind: "deposit", origin_url: window.location.origin });
          toast.success("Booking saved — taking you to secure card payment…");
          window.location.assign(p.data.url);
          return;
        } catch (e) { toast.error(e?.response?.data?.detail || "Card payment isn't available right now — your booking is saved; pay by invoice below."); }
      } else toast.success("Booking request sent — your invoice is ready below.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send that booking.");
    } finally { setBusy(false); }
  };

  const payNow = async () => {
    if (!done) return;
    setBusy(true);
    try {
      const paid = Number(done.amount_paid || 0);
      const p = await axios.post(`${API}/private-pickings/bookings/${done.id}/pay`, { email: done.email, amount_kind: paid > 0 ? "balance" : "deposit", origin_url: window.location.origin });
      window.location.assign(p.data.url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Card payment isn't available right now."); setBusy(false); }
  };
  const invoiceUrl = done ? `${API}/private-pickings/bookings/${done.id}/invoice.pdf?email=${encodeURIComponent(done.email)}` : "";

  return (
    <DahliasShell>
      <section id="book" data-testid="private-pickings-page" className="relative overflow-hidden border-b-[3px] border-[var(--berry)]/40 bg-[var(--dahlias-cream)]">
        <div className="relative max-w-5xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-12 text-center">
          <span className="inline-flex items-center gap-3 px-4 py-1.5 wobble-pill bg-white/75 font-display tracking-[0.35em] text-xs text-[var(--berry-deep)]">
            ✦ PRIVATE PICKING AT HERITAGE ✦
          </span>
          <h1 className="font-display text-4xl md:text-6xl text-[var(--dahlias-forest)] mt-6 leading-[1.05]">
            Have Heritage <span className="font-display-italic text-[var(--berry)]">to yourself.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-[var(--dahlias-ink)] max-w-2xl mx-auto leading-relaxed">
            Our Private Picking experience is a fully hosted, private visit to the farm where you can slow down,
            wander the gardens and pick your own dahlias straight from the plants.
          </p>
          <p className="mt-4 font-display-italic text-xl text-[var(--berry)]" data-testid="private-evening-text">
            {info?.evening_text || "Start in the sun. Finish under the stars."}
          </p>
        </div>
      </section>

      {/* Calendar first */}
      <section id="private-calendar" className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16" data-testid="private-calendar-section">
        <div className="font-label tracking-[0.4em] text-[10px] text-[var(--berry)]">✦ PICK YOUR DAY ✦</div>
        <h2 className="font-display text-3xl md:text-4xl text-[var(--dahlias-forest)] mt-2">Choose a date on the calendar</h2>
        <p className="mt-2 text-base text-[var(--dahlias-ink)]/75 max-w-2xl">
          Tap a day to see its sessions. Private Pickings are the whole farm for your group; Buddy Bookings are shared sessions for pairs.
          No bookable session? Request the date and we'll see what we can do. All times are New Zealand time.
        </p>
        <div className="mt-6 bg-[var(--dahlias-paper)] border-[3px] border-[var(--berry)]/40 p-4 md:p-6">
          <BookingCalendar onPickSlot={pickSlot} onPickBuddy={pickBuddy} selectedId={pick?.id} buddyMode={buddyMode} onBuddyModeChange={setBuddyMode} />
        </div>
      </section>

      <BuddyBookings buddy={info?.buddy} onFind={findBuddy} />
      <VisitTiles />

      <PatternBand pattern={2} variant="thin" tint={0.3} testid="private-band-1" />

      <section className="max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-16 grid gap-10 md:grid-cols-2" data-testid="private-story">
        <div className="space-y-5 text-lg leading-relaxed text-[var(--dahlias-ink)]">
          <p>
            Your booking includes a full tour of Heritage, our gardens and growing spaces, along with the knowledge we
            have gathered and inherited through generations of growing flowers. We'll share how we grow, pick, care for
            and work with our dahlias, plus all the little things you only learn from years spent in the garden.
          </p>
          <p>
            This is full service. The farm is yours for your booking, with your hosts there to look after you throughout
            the experience. Toilets, refreshments, grazing, buckets, snips and everything you need for picking are provided.
          </p>
        </div>
        <div className="space-y-5">
          <div className="bg-[var(--dahlias-paper)] border-[3px] border-[var(--berry)]/50 p-6 wobble-a" data-testid="private-blooms">
            <div className="font-label tracking-[0.4em] text-[10px] text-[var(--berry)]">✦ YOUR BLOOMS ✦</div>
            <ul className="mt-4 space-y-2 text-lg text-[var(--dahlias-ink)]">
              <li className="flex items-start gap-2.5">
                <Flower2 className="w-5 h-5 mt-1 text-[var(--berry)] shrink-0" />
                <span>Groups of up to 4 guests receive <strong>20 dahlias</strong> to pick and take home.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Flower2 className="w-5 h-5 mt-1 text-[var(--berry)] shrink-0" />
                <span>Groups of 5 or more receive <strong>40 dahlias</strong> to pick and take home.</span>
              </li>
            </ul>
            <p className="mt-4 font-display-italic text-xl text-[var(--dahlias-forest)]">Want more? Absolutely.</p>
            <p className="mt-1 text-base text-[var(--dahlias-ink)]/80">
              Extra blooms, buckets, vases, food and other add-ons can be added to your booking.
            </p>
          </div>
          <div className="bg-white border border-[var(--berry)]/25 p-6" data-testid="private-over-75">
            <div className="inline-flex items-center gap-2 font-display text-2xl text-[var(--dahlias-forest)]"><Gift className="w-6 h-6 text-[var(--berry)]" /> {info?.over_75_copy || "Bringing someone over 75? Their small bloom bucket is on us..."}</div>
            <p className="mt-2 text-base text-[var(--dahlias-ink)]/85 leading-relaxed">
              Tell us how many of your guests are over 75 when you book and we'll have a complimentary small bloom bucket ready for each of them. Admission stays the same.
            </p>
          </div>
        </div>
      </section>

      <PatternBand pattern={4} variant="thin" tint={0.25} testid="private-band-2" />

      <section className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16 grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          {done ? (
            <DonePanel done={done} info={info} payState={payState} busy={busy} onPay={payNow} invoiceUrl={invoiceUrl} />
          ) : (
            <div id="private-booking-form" className="bg-[var(--dahlias-paper)] border-[3px] border-[var(--berry)]/60 p-6 md:p-8 space-y-6">
              <h2 className="font-display text-3xl text-[var(--dahlias-forest)]">{isBuddy ? "Book your Buddy session" : "Book your picking"}</h2>

              <Field icon={CalendarDays} label="Your session">
                {pick ? (
                  <div className={`p-4 border-2 ${isBuddy ? "border-[var(--berry)] bg-white" : "border-[var(--dahlias-forest)]/40 bg-white"}`} data-testid="private-selected-session">
                    <div className="font-display text-xl text-[var(--dahlias-forest)] inline-flex items-center gap-2">
                      {isBuddy && <Heart className="w-5 h-5 text-[var(--berry)]" />} {prettyDate(pick.date)} · {pick.time}{pick.end ? `–${pick.end}` : ""}
                    </div>
                    <div className="text-sm text-[var(--dahlias-ink)]/70 mt-1">
                      {isBuddy ? `Buddy Booking for two · ${pick.remaining} of ${info?.buddy?.max_pairs || 3} pair places left · NZ$${buddyPrice} per pair` : "Private Picking — the farm is yours for 2 hours"}
                    </div>
                    <button type="button" onClick={() => { setPick(null); scrollTo("private-calendar"); }} data-testid="private-change-session" className="mt-2 text-sm underline text-[var(--berry-deep)]">Change date or session</button>
                  </div>
                ) : (
                  <p className="text-base italic text-[var(--dahlias-ink)]/70" data-testid="private-no-session">
                    Choose a day on the calendar above to pick your session.{" "}
                    <button type="button" onClick={() => scrollTo("private-calendar")} className="underline text-[var(--berry-deep)]">Back to the calendar</button>
                  </p>
                )}
              </Field>

              <Field icon={Users} label={isBuddy ? "Who's coming" : "Paid guests"}>
                {isBuddy ? (
                  <p className="text-base text-[var(--dahlias-ink)]" data-testid="private-buddy-guests">Two of you — <strong>NZ${buddyPrice.toFixed(0)} for the pair</strong>. Your own picking area, sharing the session with up to {(info?.buddy?.max_pairs || 3) - 1} other pairs.</p>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-2">
                      {(info?.pricing || []).map((p) => (
                        <button key={p.paid_guests} type="button" onClick={() => setGuests(p.paid_guests)}
                          data-testid={`private-guests-${p.paid_guests}`}
                          className={`px-4 h-11 wobble-pill text-base transition-colors ${guests === p.paid_guests
                            ? "bg-[var(--dahlias-forest)] text-white" : "bg-white text-[var(--dahlias-ink)] border border-[var(--berry)]/30"}`}>
                          {p.paid_guests} people — ${p.price}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-sm text-[var(--dahlias-ink)]/60">
                      Minimum {info?.min_guests || 3}, maximum {info?.max_guests || 10} paid guests. Your group picks{" "}
                      <strong>{(info?.pricing || []).find((p) => p.paid_guests === guests)?.blooms || (guests <= 4 ? 20 : 40)} dahlias</strong>{" "}
                      to take home. Just the two of you? See our Buddy Bookings — $250 for two on scheduled dates.
                    </p>
                  </>
                )}
              </Field>

              {!isBuddy && (
                <Field icon={Baby} label="Children under 12 (free)">
                  <input type="number" min="0" max="20" value={children}
                    onChange={(e) => setChildren(Math.max(0, Number(e.target.value) || 0))}
                    data-testid="private-children"
                    className="h-11 w-28 px-3 border border-[var(--berry)]/30 bg-white text-base" />
                  <p className="mt-2 text-sm text-[var(--dahlias-ink)]/60">Kids under 12 are free and very welcome.</p>
                </Field>
              )}

              <Field icon={Gift} label="Guests over 75">
                <p className="mb-2 text-base text-[var(--dahlias-ink)]">{info?.over_75_copy || "Bringing someone over 75? Their small bloom bucket is on us..."}</p>
                <input type="number" min="0" max={guestCount} value={over75}
                  onChange={(e) => setOver75(Math.max(0, Math.min(guestCount, Number(e.target.value) || 0)))}
                  data-testid="private-over-75-input"
                  className="h-11 w-28 px-3 border border-[var(--berry)]/30 bg-white text-base" />
                <p className="mt-2 text-sm text-[var(--dahlias-ink)]/60" data-testid="private-bucket-note">
                  {buckets > 0 ? `${buckets} complimentary small bloom bucket${buckets === 1 ? "" : "s"} will be ready for you — no change to your price.` : "How many of your guests are over 75? One free small bucket each, up to your guest count."}
                </p>
              </Field>

              <Field icon={Check} label="Every booking includes">
                <ul className="grid sm:grid-cols-2 gap-1.5" data-testid="private-included">
                  {(info?.included || []).map((i) => (
                    <li key={i} className="flex items-start gap-2 text-base text-[var(--dahlias-ink)]">
                      <Check className="w-4 h-4 mt-1 text-[var(--berry)] shrink-0" /> {i}
                    </li>
                  ))}
                </ul>
              </Field>

              <Field icon={Flower2} label="Optional extras">
                <p className="mb-2 text-sm text-[var(--dahlias-ink)]/60">On top of what's already included above.</p>
                <div className="space-y-2">
                  {(info?.extras || []).map((e) => (
                    <label key={e.key} data-testid={`private-extra-${e.key}`}
                      className={`flex items-center gap-3 px-4 py-3 border cursor-pointer transition-colors ${extras.includes(e.key)
                        ? "border-[var(--berry)] bg-white" : "border-[var(--berry)]/25 bg-white/60"}`}>
                      <input type="checkbox" checked={extras.includes(e.key)} onChange={() => toggleExtra(e.key)} className="w-4 h-4" />
                      <span className="flex-1 text-base text-[var(--dahlias-ink)]">{e.label}</span>
                      <span className="font-display text-lg text-[var(--berry)]">${e.price}</span>
                    </label>
                  ))}
                </div>
              </Field>

              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="private-name" />
                <Input label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="private-email" type="email" />
                <Input label="Phone (optional)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="private-phone" />
                <Input label="Anything we should know?" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="private-notes" />
                <div className="flex gap-2 items-end">
                  <Input label="Promo code (optional)" value={promo.code} onChange={(v) => setPromo({ code: v.toUpperCase(), applied: null, busy: false })} testid="private-promo" />
                  <button type="button" onClick={checkPromo} disabled={promo.busy || !promo.code.trim()} data-testid="private-promo-apply"
                    className="h-11 px-4 rounded-full border-2 border-[var(--berry)] text-[var(--berry)] text-xs font-label tracking-[0.2em] uppercase disabled:opacity-40">
                    {promo.busy ? "…" : promo.applied ? "Applied ✓" : "Apply"}
                  </button>
                </div>
              </div>

              <Field icon={CreditCard} label="How would you like to pay?">
                <div className="grid sm:grid-cols-2 gap-2">
                  {[["card", "Pay deposit by card now", "Secure Stripe checkout for your 50% deposit — your date is held straight away."],
                    ["invoice", "Pay by invoice", "We'll issue a Heritage invoice; pay the deposit by bank transfer. Your booking stays pending until it's received."]].map(([k, t, s]) => (
                    <button key={k} type="button" onClick={() => setPayMethod(k)} data-testid={`private-pay-${k}`}
                      className={`text-left p-4 border-2 transition-colors ${payMethod === k ? "border-[var(--berry)] bg-white" : "border-[var(--berry)]/25 bg-white/60"}`}>
                      <div className="font-display text-lg text-[var(--dahlias-forest)] inline-flex items-center gap-2">{k === "card" ? <CreditCard className="w-4 h-4" /> : <FileText className="w-4 h-4" />} {t}</div>
                      <div className="text-sm text-[var(--dahlias-ink)]/70 mt-1">{s}</div>
                    </button>
                  ))}
                </div>
              </Field>

              <div className="flex flex-wrap items-center justify-between gap-4 border-t border-[var(--berry)]/20 pt-5">
                <div>
                  <div className="font-label tracking-[0.3em] text-[10px] text-[var(--dahlias-ink)]/60">TOTAL</div>
                  <div className="font-display text-4xl text-[var(--berry)]" data-testid="private-total">NZ${payable.toFixed(2)}</div>
                  {discount > 0 && <div className="text-xs text-[#2F6B4F] font-semibold" data-testid="private-discount">Was NZ${total.toFixed(2)} · {promo.applied.code} saves NZ${discount.toFixed(2)}</div>}
                  {buckets > 0 && <div className="text-xs text-[#2F6B4F] font-semibold" data-testid="private-total-buckets">+ {buckets} complimentary small bloom bucket{buckets === 1 ? "" : "s"} (NZ$0.00)</div>}
                  <div className="mt-1 text-sm text-[var(--dahlias-ink)]/80" data-testid="private-deposit">
                    {depositPct}% deposit today: <strong>NZ${deposit.toFixed(2)}</strong> · balance NZ${(payable - deposit).toFixed(2)} due 72 hours before
                  </div>
                </div>
                <button type="button" onClick={submit} disabled={busy || !pick} data-testid="private-submit"
                  className="px-7 h-14 wobble-pill bg-[var(--berry)] text-white font-display text-lg inline-flex items-center gap-2 disabled:opacity-40">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />} {isBuddy ? "Book our Buddy session" : "Book my picking"}
                </button>
              </div>
              <p className="text-sm text-[var(--dahlias-ink)]/70">{info?.change_note}</p>
            </div>
          )}
        </div>

        <aside className="space-y-6">
          <div className="bg-[var(--dahlias-paper)] border-[3px] border-[var(--dahlias-forest)]/40 p-6 wobble-b" data-testid="private-terms">
            <div className="font-label tracking-[0.4em] text-[10px] text-[var(--dahlias-forest)]">✦ BOOKING &amp; PAYMENT ✦</div>
            <ul className="mt-4 space-y-2.5">
              {(info?.terms || []).map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-base text-[var(--dahlias-ink)] leading-relaxed">
                  <Check className="w-5 h-5 mt-1 text-[var(--berry)] shrink-0" /> {t}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-base text-[var(--dahlias-ink)]/80 italic" data-testid="private-payment-note">
              Pay your deposit by card (Stripe) at checkout, or choose an invoice and pay by bank transfer.
            </p>
            {info?.payment?.bank_account && (
              <p className="mt-2 text-sm text-[var(--dahlias-ink)]/70">Bank transfer: {info.payment.bank_account}</p>
            )}
          </div>
          <div className="bg-white border border-[var(--berry)]/25 p-6">
            <div className="font-label tracking-[0.4em] text-[10px] text-[var(--berry)]">✦ PRICING ✦</div>
            <ul className="mt-3 space-y-1.5 text-lg text-[var(--dahlias-ink)]" data-testid="private-pricing-list">
              <li className="flex justify-between border-b border-dashed border-[var(--berry)]/20 pb-1" data-testid="private-pricing-buddy">
                <span className="inline-flex items-center gap-1.5"><Heart className="w-4 h-4 text-[var(--berry)]" /> Buddy Booking for 2 <span className="text-sm opacity-60">· scheduled dates</span></span><strong>${buddyPrice.toFixed(0)}</strong>
              </li>
              {(info?.pricing || []).map((p) => (
                <li key={p.paid_guests} className="flex justify-between border-b border-dashed border-[var(--berry)]/20 pb-1">
                  <span>{p.paid_guests} people <span className="text-sm opacity-60">· {p.blooms || (p.paid_guests <= 4 ? 20 : 40)} blooms</span></span><strong>${p.price}</strong>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-sm italic text-[var(--dahlias-ink)]/70">Under 12s are free. Guests over 75 get a complimentary small bloom bucket. Evening bookings from 6pm — start in the sun, finish under the stars. Special bookings welcome.</p>
          </div>
        </aside>
      </section>
      <FarmExperience />
      <WhoYouMightMeet />
      <OpenDaysSection />
    </DahliasShell>
  );
}

function DonePanel({ done, info, payState, busy, onPay, invoiceUrl }) {
  const paid = Number(done.amount_paid || 0);
  const outstanding = Math.max(0, Number(done.total || 0) - paid);
  const isBuddy = done.kind === "buddy";
  return (
    <div id="private-booking-form" className="bg-[var(--dahlias-paper)] border-[3px] border-[var(--berry)] p-7 wobble-a" data-testid="private-booking-done">
      <div className="font-label tracking-[0.4em] text-[10px] text-[var(--berry)]">✦ {paid > 0 ? "BOOKING RECEIVED" : "BOOKING REQUEST SENT"} ✦</div>
      <h2 className="font-display text-3xl text-[var(--dahlias-forest)] mt-3">Thank you, {done.customer_name.split(" ")[0]}.</h2>
      <p className="mt-3 text-lg text-[var(--dahlias-ink)]/80" data-testid="private-done-summary">
        Reference <strong>{done.reference}</strong> — {prettyDate(done.date)} at {done.time}{done.session_end ? `–${done.session_end}` : ""}.
        {" "}{isBuddy ? "Buddy Booking for two" : `${done.paid_guests} paid guests${done.children ? ` + ${done.children} under 12` : ""}`}, total NZ${Number(done.total).toFixed(2)}.
      </p>
      {Number(done.complimentary_buckets) > 0 && (
        <p className="mt-2 text-base text-[#2F6B4F] font-semibold inline-flex items-center gap-2" data-testid="private-done-buckets">
          <Gift className="w-4 h-4" /> {done.complimentary_buckets} complimentary small bloom bucket{done.complimentary_buckets === 1 ? "" : "s"} for your guest{done.complimentary_buckets === 1 ? "" : "s"} over 75 — on us.
        </p>
      )}
      <div className="mt-4 p-4 bg-white border border-[var(--berry)]/30" data-testid="private-done-payment">
        <div className="font-label tracking-[0.3em] text-[10px] text-[var(--dahlias-forest)]">{outstanding <= 0 ? "PAID IN FULL" : paid > 0 ? "DEPOSIT RECEIVED" : "TO SECURE YOUR DATE"}</div>
        {payState === "checking" && <p className="mt-2 text-base inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Checking your card payment…</p>}
        {payState === "cancelled" && <p className="mt-2 text-base text-[#A33030]">Card payment was cancelled — your booking is saved. Pay below or by invoice.</p>}
        <p className="mt-2 text-lg text-[var(--dahlias-ink)]" data-testid="private-done-amounts">
          {paid > 0 && <>Paid so far: <strong>NZ${paid.toFixed(2)}</strong> · </>}
          {outstanding > 0 ? <>{paid > 0 ? "Balance" : `${Number(done.deposit_percent || 50)}% deposit`}: <strong>NZ${(paid > 0 ? outstanding : Number(done.deposit_due ?? done.total / 2)).toFixed(2)}</strong>{paid === 0 && <> · balance NZ${Number(done.balance_due ?? done.total / 2).toFixed(2)} due 72 hours before</>}</> : <>Nothing more to pay — see you among the dahlias.</>}
        </p>
        {outstanding > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" onClick={onPay} disabled={busy} data-testid="private-done-stripe"
              className="inline-flex items-center gap-2 h-12 px-5 wobble-pill bg-[var(--berry)] text-white font-display text-base disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Pay {paid > 0 ? "balance" : "deposit"} by card
            </button>
            <a href={invoiceUrl} target="_blank" rel="noopener noreferrer" data-testid="private-done-invoice"
              className="inline-flex items-center gap-2 h-12 px-5 wobble-pill border-2 border-[var(--dahlias-forest)] text-[var(--dahlias-forest)] font-display text-base">
              <FileText className="w-4 h-4" /> Download invoice (PDF)
            </a>
          </div>
        )}
        {outstanding <= 0 && (
          <a href={invoiceUrl} target="_blank" rel="noopener noreferrer" data-testid="private-done-receipt"
            className="mt-3 inline-flex items-center gap-2 h-12 px-5 wobble-pill border-2 border-[var(--dahlias-forest)] text-[var(--dahlias-forest)] font-display text-base">
            <FileText className="w-4 h-4" /> Download paid receipt (PDF)
          </a>
        )}
        {info?.payment?.bank_account && outstanding > 0 && (
          <p className="mt-3 text-sm text-[var(--dahlias-ink)]/70">Bank transfer: {info.payment.bank_account} · reference {done.reference}</p>
        )}
      </div>
      <p className="mt-3 text-base text-[var(--dahlias-ink)]/70 italic">
        We'll email you to confirm. You can also see this booking in your Heritage HQ under Private Bookings.
      </p>
      <p className="mt-3 text-sm text-[var(--berry-deep)]">{info?.change_note}</p>
    </div>
  );
}

const Field = ({ icon: Icon, label, children }) => (
  <div>
    <div className="inline-flex items-center gap-2 font-label tracking-[0.3em] text-[10px] text-[var(--dahlias-forest)] mb-2">
      <Icon className="w-4 h-4" /> {label.toUpperCase()}
    </div>
    {children}
  </div>
);

const Input = ({ label, value, onChange, testid, type = "text" }) => (
  <label className="block">
    <span className="block font-label tracking-[0.3em] text-[10px] text-[var(--dahlias-ink)]/70 mb-1">{label.toUpperCase()}</span>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full h-11 px-3 border border-[var(--berry)]/30 bg-white text-base" />
  </label>
);
